import { action, observable, runInAction, set, configure } from "mobx";
import {
	TProductModule,
	TProductElement,
	GetProductsResp,
	LoadPoolProps,
} from "../../../services/product";
import { isNotBlankArray } from "utils/array";

import { GET_PRODUCTS_LIMIT } from "utils/playground/product";

configure({ isolateGlobalState: true, enforceActions: "observed" });
export default class ProductStore {
	// 商品信息
	@observable public productsListMap: Record<string, TProductElement[]> = {};
	// 是否加载完成
	@observable public isLoadEnd: Record<string, boolean> = {};
	// 是否正在加载商品
	@observable public isLoadingProducts: Record<string, boolean> = {};
	// 能否无限加载
	@observable public productsLoadMore: Record<string, boolean> = {};
	// 后台设置的商品展示数量
	@observable private productsLimit: Record<string, number> = {};
	// 商品已请求数量
	@observable private offset: Record<string, number> = {};
	/** 记录已经被曝光的产品gid */
	@observable public exposureProductListMap: Record<string, string[]> = {};

	constructor(modules: TProductModule[]) {
		modules.forEach((item) => {
			const hasElements = isNotBlankArray(item.productElements);
			const mapKey = `${item.poolId}`;
			// set product elements by poolId
			set(this.productsListMap, {
				[mapKey]: item.productElements || [],
			});
			// 设置商品池是否可以无限加载
			set(this.productsLoadMore, { [mapKey]: item.canLoadMore });

			const maxLimit = item.maxLoad;

			// 初始化已请求数量
			set(this.offset, {
				[mapKey]: hasElements ? Math.min(GET_PRODUCTS_LIMIT, maxLimit) : 0,
			});
			set(this.isLoadEnd, {
				[mapKey]: hasElements ? item.productElements.length === maxLimit : false,
			});
			if (!item.canLoadMore) {
				// 设置后台设置的商品展示数量
				set(this.productsLimit, { [mapKey]: maxLimit });
			} else {
				// 如果为无限加载，则设置单次请求数量为 20
				set(this.productsLimit, { [mapKey]: GET_PRODUCTS_LIMIT });
			}
		});
	}
	@action
	getLimit = (poolId: string) => {
		if (this.productsLoadMore[poolId]) {
			return this.productsLimit[poolId];
		}
		const productLimit: number =
			this.productsLimit[poolId] - (this.productsListMap[poolId]?.length || 0);
		return Math.min(productLimit, GET_PRODUCTS_LIMIT);
	};

	@action
	loadProductsList = async (
		poolId: string,
		onLoadPool: (props: LoadPoolProps) => Promise<GetProductsResp>,
	) => {
		if (this.isLoadingProducts[poolId] || this.isLoadEnd[poolId]) {
			return;
		}

		set(this.isLoadingProducts, { [poolId]: true });
		try {
			const realLimit: number = this.getLimit(poolId);
			const reqLimit = Math.max(realLimit, GET_PRODUCTS_LIMIT);
			const res = await onLoadPool({
				poolId,
				limit: `${reqLimit}`,
				offset: (this.offset[poolId] || 0).toString(),
				prmPosition: this.productsListMap[poolId]?.length || 0,
			});

			if (isNotBlankArray(res?.products)) {
				runInAction("update product list", () => {
					// 商品已请求数量 = 累计请求数量 + 本次请求数量
					set(this.offset, {
						[poolId]: this.offset[poolId] + realLimit,
					});
					let products = res.products;
					if (products.length > realLimit) {
						products = products.slice(0, realLimit);
					}
					if (!this.productsListMap[poolId]) {
						set(this.productsListMap, { [poolId]: products });
					} else {
						set(this.productsListMap, {
							[poolId]: this.productsListMap[poolId].concat(products),
						});
					}

					/**
					 * 不再加载的触发条件：
					 * 1. 接口返回的 hasNext 为 false 时
					 * 2. 在商品池非无限加载的情况下，接口返回的商品数量累计值 >= 商品池上限
					 */
					if (
						!res.hasNext ||
						(this.productsListMap[poolId] &&
							this.productsListMap[poolId].length >= this.productsLimit[poolId] &&
							!this.productsLoadMore[poolId])
					) {
						set(this.isLoadEnd, { [poolId]: true });
					}
				});
			} else {
				runInAction(() => {
					set(this.isLoadEnd, { [poolId]: true });
				});
			}
			return res?.products;
		} catch (e) {
			console.error("GetProducts Got error", e);
		} finally {
			runInAction(() => {
				set(this.isLoadingProducts, { [poolId]: false });
			});
		}
		return [];
	};

	@action
	handleExposureProductList = (mapKey: string, productList: string[]) => {
		if (mapKey) {
			set(this.exposureProductListMap, { [mapKey]: productList });
		}
	};
}
